import { useEffect } from 'react'
import {
  GrowthBook,
  GrowthBookProvider,
  useFeature,
} from '@growthbook/growthbook-react'
import { useSelector } from 'react-redux'

import { growthbookApiKey } from 'config'
import { logEvent } from 'lib/analytics'
import { useCurrentUser, useGetUserInterestsQuery } from 'store/api/yay/user'
import { selectTimelineFilters } from 'store/slices/timeline'

// Create a GrowthBook instance
const growthbook = new GrowthBook({
  apiHost: 'https://growthbook-cdn.yay.space',
  clientKey: growthbookApiKey,
  // Callback when a user is put into an A/B experiment
  trackingCallback: (experiment, result) => {
    logEvent.gb_experiment_view({
      experiment_id: experiment.key,
      variation_id: result.variationId,
    })
  },
})

export const FeatureFlagProvider = ({ children }) => {
  const { data: currentUser } = useCurrentUser()
  const filters = useSelector(selectTimelineFilters)
  const { data: userInterests } = useGetUserInterestsQuery(
    {},
    { skip: !currentUser?.id }
  )

  useEffect(() => {
    if (growthbookApiKey) {
      // Load feature definitions from GrowthBook API
      growthbook.loadFeatures({ autoRefresh: true })
    }
  }, [])

  useEffect(() => {
    if (currentUser && userInterests) {
      const faveIds = userInterests.filter((x) => x.selected).map((x) => x.id)

      // Set user attributes for targeting (from cookie, auth system, etc.)
      growthbook.setAttributes({
        // Yay user_id: str
        userId: currentUser?.id?.toString(),
        // If new user -> True, else False: bool
        newUser: currentUser?.new_user,
        // If this device has multiple Accounts on device -> True: bool
        //  "multipleId": false, // Not need to send from Web
        interestsCount: currentUser?.interests_count,
        isPublicTimelineInterestsFilterEnabled: filters?.sharedInterests,
        // Platform - one of ["iOS", "Android", "Web"]: str
        platform: 'Web',
        faveIds,
      })
    }
  }, [currentUser, userInterests])

  return (
    <GrowthBookProvider growthbook={growthbook}>{children}</GrowthBookProvider>
  )
}

export type FavFilterGrowthBookEnabledType =
  | 'user_adjustable'
  | 'forced_on'
  | 'forced_off'

export const useFavFilterGrowthBookEnabled = () => {
  const timelineFilters = useSelector(selectTimelineFilters)

  const isFavFilterGrowthBookEnabled =
    useFeature<FavFilterGrowthBookEnabledType>('yay-13642')

  if (isFavFilterGrowthBookEnabled?.value == 'forced_on') {
    return true
  }

  if (isFavFilterGrowthBookEnabled?.value == 'forced_off') {
    return false
  }

  return timelineFilters?.sharedInterests
}
